import { FC, useEffect, useMemo, useState } from 'react';
import { UiFilesUpload, UiText, UiIcon } from '@frontend/znanie-uikit';

import { formatBytes, decl } from 'utils/helpers';

import * as U from './UploadFiles.utils';
import * as C from './UploadFiles.const';
import * as T from './UploadFiles.types';

import css from './UploadFiles.module.scss';

export const UploadFiles: FC<T.IUploadFilesProps> = (props) => {
  const {
    defaultFiles,
    label,
    errorLabel,
    onUpload,
    onFilesError,
    multipleLimit = C.DEFAULT_FILES_LIMIT,
    sizeLimit = C.DEFAULT_SIZE_LIMIT,
  } = props;
  const [files, setFiles] = useState<File[]>(defaultFiles || []);
  const [error, setError] = useState<string | null>(null);
  const [hasExceedingLimit, setHasExceedingLimit] = useState<boolean>(false);

  const exceedingLimitFiles: File[] = useMemo(
    () => files.filter((file) => U.checkSizeLimit(file.size, sizeLimit)),
    [files, sizeLimit]
  );

  const onUploadFiles = async (fileList: FileList | null): Promise<void> => {
    resetError();

    if (fileList) {
      //если количество уже загруженных файлов больше чем значение multipleLimit
      const hasOverloadLimit: boolean = !(
        multipleLimit - files.length >=
        Array.from(fileList).length
      );

      if (
        !(
          (Array.from(fileList).length > multipleLimit || hasOverloadLimit) &&
          multipleLimit > C.DEFAULT_FILES_LIMIT
        )
      ) {
        Array.from(fileList).forEach((file) => {
          //если файл существует и не был загружен в массив files
          if (file && !files.some((f) => f.name === file.name)) {
            //проверяем, можем ли мы загружать файлы, если количество уже загруженных файлов не больше чем multipleLimit
            if (!hasOverloadLimit) setFiles((files) => [...files, file]);
          }
        });
      } else {
        setError(
          `*Превышен лимит загружаемых файлов. Доступно к загрузке максимум ${multipleLimit} ${decl(
            multipleLimit,
            ['файл', 'файла', 'файлов']
          )}`
        );
      }
    }
  };

  const resetError = (): void => setError(null);

  const clearFile = (fileName: string): void => {
    const list: File[] = files.filter((f) => f.name !== fileName);
    setFiles(list);
    resetError();
  };

  const clearExceedingLFiles = () => {
    setFiles(files.filter((file) => !U.checkSizeLimit(file.size, sizeLimit)));
    resetError();
  };

  useEffect(() => {
    onUpload && onUpload(files);
    setHasExceedingLimit(!!exceedingLimitFiles.length);
  }, [files.length]);

  useEffect(() => {
    onFilesError && onFilesError(error);
  }, [error]);

  useEffect(() => {
    if (exceedingLimitFiles.length >= 2) {
      setError(
        `*Загруженные файлы имеют недопустимый размер. Максимум ${sizeLimit} МБ`
      );
    } else if (exceedingLimitFiles.length === 1) {
      setError(
        `*Очистите поле от файла, превышающего размер в ${sizeLimit} МБ`
      );
    }
  }, [hasExceedingLimit, exceedingLimitFiles]);

  return (
    <div className={css.block}>
      {label && (
        <UiText className={css.title} size="small_plus">
          {label}
        </UiText>
      )}
      <UiFilesUpload
        {...props}
        maxLength={5}
        variant="outline"
        onUpload={onUploadFiles}
        multiple
        disabled={files.length >= multipleLimit}
        accept="application/pdf"
      />
      {!!files.length && (
        <>
          {files.map((file: File, i) => (
            <div key={i}>
              <div className={css.fileInfo}>
                <UiIcon className={css.fileMarkIcon} name="file" size={20} />
                <div className={css.fileName}>
                  <UiText size="small_x">{file.name}</UiText>
                  <UiText size="small_x" color="grey">
                    {formatBytes(file.size)}
                  </UiText>
                </div>
                <UiIcon
                  className={css.fileDeleteIcon}
                  name="close"
                  size={10}
                  color="#666C77"
                  onClick={() => clearFile(file.name)}
                />
              </div>
              {U.checkSizeLimit(file.size, sizeLimit) && (
                <UiText className={css.errorLabel} as="p" size="small_x">
                  *Файл не должен быть больше {sizeLimit} МБ
                </UiText>
              )}
            </div>
          ))}
        </>
      )}
      {errorLabel && (
        <UiText className={css.errorLabel} as="p" size="small_x">
          {errorLabel}
        </UiText>
      )}
      {error && (
        <UiText className={css.errorLabel} as="p" size="small_x">
          {error}
        </UiText>
      )}
      {hasExceedingLimit && (
        <UiText
          onClick={clearExceedingLFiles}
          className={css.clearExceedingLFiles}
          as="span"
          size="small_x"
          color="grey"
        >
          Очистить
        </UiText>
      )}
    </div>
  );
};
